import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { ClientManagerInstanceToken } from '../../interfaces/client-manager.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { UserMasterService } from '../../services/user-master/user-master.service';
import {
    NotificationIdentifierModel,
    ODataNotificationIdentifierOutModel,
} from '../low-level/notification-identifier.model';
import { NotificationModel } from '../low-level/notification.model';
import { TableModelOptions } from '../options/table-model-options';
import { ColumnModel } from '../primitive/column.model';
import { TextModel } from '../primitive/text.model';
import { ArrayModel } from './array.model';
import { CompositeModel } from './composite.model';
import {
    DataMaintenanceEquipmentModel,
    ODataDataMaintenanceEquipmentOutModel,
} from './data-maintenance-equipment.model';
import { TableModel } from './table.model';

export class DataMaintenanceModel extends CompositeModel {
    public notificationIdentifier: NotificationIdentifierModel;
    public name1: TextModel;
    public name2: TextModel;
    public street: TextModel;
    public city: TextModel;
    public phone: TextModel;
    public faxNr1: TextModel;
    public faxNr2: TextModel;
    public custPONo: TextModel;
    public functionalLoc: TextModel;
    public technicalIdNum: TextModel;
    public reasonText: TextModel;

    public equipmentSet: ArrayModel<DataMaintenanceEquipmentModel>;
    public tableModel: TableModel<DataMaintenanceEquipmentModel>;
    public constructor(private notificationModel: NotificationModel) {
        super('DataMaintenanceModel');

        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const translate = injector.get(TranslateService);
        const userMasterService = injector.get(UserMasterService);
        const clientManager = injector.get(ClientManagerInstanceToken);

        this.notificationIdentifier = this.notificationModel.identifier;

        this.name1 = <TextModel>this.set(new TextModel('Name1', notificationModel.address.name1.value));
        modelOptionsLoader.load(this.name1, 'DataMaintenanceContact', 'ContactPersonFirstname');

        this.name2 = <TextModel>this.set(new TextModel('Name2', notificationModel.address.name2.value));
        modelOptionsLoader.load(this.name2, 'DataMaintenanceContact', 'ContactPersonLastname');

        this.street = <TextModel>this.set(new TextModel('Street', notificationModel.address.street.value));
        modelOptionsLoader.load(this.street, 'DataMaintenanceContact', 'Street');

        this.city = <TextModel>this.set(new TextModel('City', notificationModel.address.city.value));
        modelOptionsLoader.load(this.city, 'DataMaintenanceContact', 'City');

        this.faxNr1 = <TextModel>this.set(new TextModel('FaxNumber1', notificationModel.address.fax1.value));
        modelOptionsLoader.load(this.faxNr1, 'DataMaintenanceContact', 'FaxEmail1');

        this.faxNr2 = <TextModel>this.set(new TextModel('FaxNumber2', notificationModel.address.fax2.value));
        modelOptionsLoader.load(this.faxNr2, 'DataMaintenanceContact', 'FaxEmail2');

        if (clientManager.isNgmAP0()) {
            if (clientManager.isNgmAA()) {
                // technical identity number is mapped with ndp header room field.
                this.technicalIdNum = <TextModel>(
                    this.set(new TextModel('Room', this.notificationModel.address.room.value))
                );
                modelOptionsLoader.load(this.technicalIdNum, 'DataMaintenanceContact', 'TechnicalIdNum');

                // add the change reason field
                this.reasonText = <TextModel>this.set(new TextModel('ReasonText'));
                modelOptionsLoader.load(this.reasonText, 'DataMaintenanceContact', 'ReasonText');
            }

            userMasterService.setSalesOrgAndPlantForModel(this);
        }

        this.phone = <TextModel>this.set(new TextModel('TelNumber', notificationModel.address.phone.value));
        modelOptionsLoader.load(this.phone, 'DataMaintenanceContact', 'Tel');

        this.custPONo = <TextModel>this.set(new TextModel('CustPONo', this.notificationModel.customerPurchaseOrderNr));
        modelOptionsLoader.load(this.custPONo, 'DataMaintenanceContact', 'CustPONo');

        this.functionalLoc = <TextModel>this.set(new TextModel('FunctionalLoc', this.notificationModel.functionalLoc));
        modelOptionsLoader.load(this.functionalLoc, 'DataMaintenanceContact', 'FunctionalLoc');

        this.equipmentSet = <ArrayModel<DataMaintenanceEquipmentModel>>this.set(
            // For AA and CN, there can be duplicate equipments due to different measuring points. So it is expected to display only unique equipments.
            new ArrayModel<DataMaintenanceEquipmentModel>(
                'EquipmentSet',
                clientManager.isNgmAA()
                    ? this.notificationModel
                          .getUniqueEquipmentsByEquipmentNumber()
                          .map((e) => new DataMaintenanceEquipmentModel(e, notificationModel.processIndicator))
                    : this.notificationModel.equipmentSet.map(
                          (e) => new DataMaintenanceEquipmentModel(e, notificationModel.processIndicator),
                      ),
            ),
        );

        const columns: ColumnModel[] = [];
        columns.push(
            new ColumnModel(<string>translate.instant('DATA_MAINTENANCE_EQUIPMENT_MATERIAL_ID'), 'matnr.value'),
        );
        columns.push(
            new ColumnModel(<string>translate.instant('DATA_MAINTENANCE_EQUIPMENT_DESCRIPTION'), 'equiShortDesc'),
        );
        columns.push(new ColumnModel(<string>translate.instant('DATA_MAINTENANCE_EQUIPMENT_SERIAL'), 'sernr.value'));
        const tableOptions = new TableModelOptions({ columns, sortColumn: 'pos' });

        this.tableModel = new TableModel<DataMaintenanceEquipmentModel>(this.equipmentSet.value, tableOptions);
    }

    public getOutModel(): ODataDataMaintenanceOutModel {
        const outModel = <ODataDataMaintenanceOutModel>super.getOutModel();
        _.merge(outModel, this.notificationIdentifier.getOutModel());
        return outModel;
    }
}

export interface ODataDataMaintenanceOutModel extends ODataNotificationIdentifierOutModel {
    Name1: string;
    Name2: string;
    Street: string;
    City: string;
    FaxNumber1: string;
    FaxNumber2: string;
    TelNumber: string;
    CustPONo: string;
    FunctionalLoc: string;
    Room: string;
    ReasonText: string;
    EquipmentSet: ODataDataMaintenanceEquipmentOutModel[];
}
